import React, { useEffect, useMemo, useRef } from 'react';
import { useGlobalStore } from 'lib/store';
import { AgreementConfig, AgreementContentItem, DEFAULT_AGREEMENT_CONFIG } from 'lib/constants/operator';
import AgreementCheckbox from 'lib/container/SMSCaptchaModal/AgreementCheckbox';
import AgreementText from 'lib/container/SMSCaptchaModal/AgreementText';
import { reportAgreementFallback } from 'lib/container/SMSCaptchaModal/agreementReport';
import { showLongTextTipsDialog } from 'lib/components/LongTextTipsDialog';
import './index.scss';

interface SMSAgreementConfigData extends Partial<AgreementConfig> {
  enabled?: boolean;
  showInPage?: boolean;
}

interface Props {
  data: SMSAgreementConfigData;
}

/**
 * 订购弹窗协议合规配置组件
 * - 默认无 UI，仅在挂载时把 dataForm 配置注入全局 store，供 SMSCaptchaModal 弹窗读取
 * - 当 showInPage=true 时，会在原位置展示一个协议勾选区预览，方便所见即所得调试
 */
function buildAgreementConfig(data: SMSAgreementConfigData): AgreementConfig {
  const rawList: AgreementContentItem[] =
    Array.isArray(data.agreements) && data.agreements.length > 0
      ? data.agreements
      : DEFAULT_AGREEMENT_CONFIG.agreements;

  const idSeen = new Set<string>();
  const normalizedList: AgreementContentItem[] = rawList
    .filter((item) => {
      if (!item || !item.id) return false;
      if (idSeen.has(item.id)) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn(`[SMSAgreementConfig] 重复的协议 id: "${item.id}"，后续条目已忽略`);
        }
        return false;
      }
      idSeen.add(item.id);
      return true;
    })
    .map((item) => ({
      id: item.id,
      title: item.title || item.id,
      content: item.content || '',
    }));

  return {
    displayText: data.displayText ?? DEFAULT_AGREEMENT_CONFIG.displayText,
    agreements: normalizedList,
    agreementVersion: data.agreementVersion || DEFAULT_AGREEMENT_CONFIG.agreementVersion,
    defaultChecked: Boolean(data.defaultChecked),
    enableReconfirm: data.enableReconfirm !== false,
    reconfirmTitle: data.reconfirmTitle ?? DEFAULT_AGREEMENT_CONFIG.reconfirmTitle,
    reconfirmConfirmText: data.reconfirmConfirmText ?? DEFAULT_AGREEMENT_CONFIG.reconfirmConfirmText,
  };
}

/**
 * 校验运管侧下发的协议配置；满足 BR-30 / 异常处理表的硬性兜底：
 * - displayText 缺失 → configMissing 降级
 * - agreementVersion 缺失 → versionMissing 降级（合规举证缺失版本号，必须按 show=false 走兜底）
 * 返回 null 表示需要按未启用处理；并已上报 EVT-24。
 */
function validateAgreementConfig(
  data: SMSAgreementConfigData,
): { ok: boolean; errorType?: 'configMissing' | 'versionMissing' } {
  if (!data || typeof data !== 'object') return { ok: false, errorType: 'configMissing' };
  if (!data.displayText) return { ok: false, errorType: 'configMissing' };
  if (!data.agreementVersion) return { ok: false, errorType: 'versionMissing' };
  return { ok: true };
}

export default function SMSAgreementConfig({ data }: Props) {
  // 用 data 的序列化结果做依赖，避免父组件反复 new 同一语义对象时不必要重算
  const dataKey = JSON.stringify(data ?? {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const config = useMemo(() => buildAgreementConfig(data ?? {}), [dataKey]);

  // 记录上一次已上报过的兜底原因，避免父组件无关字段变化导致 EVT-24 重复上报
  const lastFallbackRef = useRef<'configMissing' | 'versionMissing' | null>(null);

  useEffect(() => {
    if (!data || !data.enabled) {
      lastFallbackRef.current = null;
      useGlobalStore.getState().initGlobalData({ smsModalAgreement: null });
      return;
    }
    // 启用时校验配置，缺关键字段则按 show=false 兜底并上报 EVT-24
    const result = validateAgreementConfig(data);
    if (!result.ok && result.errorType) {
      if (lastFallbackRef.current !== result.errorType) {
        lastFallbackRef.current = result.errorType;
        reportAgreementFallback(result.errorType, {
          agreementVersion: data?.agreementVersion,
        });
      }
      useGlobalStore.getState().initGlobalData({ smsModalAgreement: null });
      return;
    }
    lastFallbackRef.current = null;
    useGlobalStore.getState().initGlobalData({ smsModalAgreement: config });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- config 由 useMemo 聚合 data 语义，避免 data 仅引用变化重复 initGlobalData
  }, [config]);

  const handlePreviewReconfirm = () => {
    showLongTextTipsDialog({
      title: config.reconfirmTitle || '温馨提示',
      content: <AgreementText config={config} previewMode className="yzf-agreement-dialog__text" />,
      buttonText: config.reconfirmConfirmText || '同意并继续',
    });
  };

  if (!data?.enabled || !data?.showInPage) return null;

  return (
    <div className="sms-agreement-config__preview">
      <div className="sms-agreement-config__preview-tag">协议勾选区预览</div>
      <div className="sms-agreement-config__preview-stage">
        {/* previewMode 关闭所有 EVT-20~EVT-29 上报，避免编辑器调试点击污染合规埋点口径 */}
        <AgreementCheckbox config={config} previewMode />
      </div>
      <button type="button" className="sms-agreement-config__preview-reconfirm-btn" onClick={handlePreviewReconfirm}>
        预览二次确认弹窗
      </button>
      <div className="sms-agreement-config__preview-tip">
        预览为弹窗内真实样式（深色背景）。线上仅在订购弹窗内展示，不会出现在页面上。
      </div>
    </div>
  );
}
